import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, Edit, FolderPlus } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import type { Category, DbProduct } from "@/hooks/useRestaurant";
import { getLangValue } from "@/types/webMenu";

type Props = {
  categories: Category[];
  menuItems: DbProduct[];
  restaurantId: string | undefined;
  onAddCategory: (cat: { name: string; name_ar?: string; icon?: string }) => Promise<{ error: any } | undefined>;
  onDeleteCategory: (id: string) => Promise<void>;
  onAddItem: (item: any) => Promise<{ error: any } | undefined>;
  onUpdateItem: (id: string, updates: any) => Promise<{ error: any } | undefined>;
  onDeleteItem: (id: string) => Promise<void>;
};

export default function AdminMenu({ categories, menuItems, restaurantId, onAddCategory, onDeleteCategory, onAddItem, onUpdateItem, onDeleteItem }: Props) {
  const { toast } = useToast();
  const [catOpen, setCatOpen] = useState(false);
  const [itemOpen, setItemOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<DbProduct | null>(null);

  // Category form
  const [catName, setCatName] = useState("");
  const [catNameAr, setCatNameAr] = useState("");
  const [catIcon, setCatIcon] = useState("🍽️");

  // Item form
  const [itemName, setItemName] = useState("");
  const [itemNameAr, setItemNameAr] = useState("");
  const [itemDesc, setItemDesc] = useState("");
  const [itemDescAr, setItemDescAr] = useState("");
  const [itemPrice, setItemPrice] = useState("");
  const [itemCategory, setItemCategory] = useState("");
  const [itemImage, setItemImage] = useState("");
  const [itemPopular, setItemPopular] = useState(false);
  const [itemAvailable, setItemAvailable] = useState(true);

  const resetItemForm = () => {
    setItemName(""); setItemNameAr(""); setItemDesc(""); setItemDescAr("");
    setItemPrice(""); setItemCategory(""); setItemImage(""); setItemPopular(false); setItemAvailable(true);
    setEditingItem(null);
  };

  const getNameAr = (item: DbProduct) => getLangValue(item.name_lang as any, "ar", item.name);
  const getDescAr = (item: DbProduct) => getLangValue(item.description_lang as any, "ar", item.description || "");

  const openEditItem = (item: DbProduct) => {
    setEditingItem(item);
    setItemName(item.name);
    setItemNameAr(getNameAr(item));
    setItemDesc(item.description || "");
    setItemDescAr(getDescAr(item));
    setItemPrice(String(item.price));
    setItemCategory(item.category_id || "");
    setItemImage(item.image_url || "");
    setItemPopular(item.best_seller || false);
    setItemAvailable(item.is_active !== false);
    setItemOpen(true);
  };

  const handleAddCategory = async () => {
    if (!catName.trim()) return;
    await onAddCategory({ name: catName, name_ar: catNameAr, icon: catIcon });
    setCatName(""); setCatNameAr(""); setCatIcon("🍽️"); setCatOpen(false);
    toast({ title: "Category added" });
  };

  const handleSaveItem = async () => {
    if (!itemName.trim() || !itemPrice) return;
    const payload = {
      name: itemName,
      name_lang: [
        { lang: "en", value: itemName },
        { lang: "ar", value: itemNameAr || itemName },
      ],
      description: itemDesc || null,
      description_lang: [
        { lang: "en", value: itemDesc || "" },
        { lang: "ar", value: itemDescAr || "" },
      ],
      price: parseFloat(itemPrice),
      category_id: itemCategory || null,
      image_url: itemImage || null,
      best_seller: itemPopular,
      is_active: itemAvailable,
    };

    if (editingItem) {
      await onUpdateItem(editingItem.id, payload);
      toast({ title: "Item updated" });
    } else {
      await onAddItem(payload);
      toast({ title: "Item added" });
    }
    resetItemForm();
    setItemOpen(false);
  };

  return (
    <div className="space-y-6">
      {/* Categories section */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Categories</CardTitle>
          <Dialog open={catOpen} onOpenChange={setCatOpen}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline"><FolderPlus className="w-4 h-4 mr-2" /> Add Category</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New Category</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div><Label>Name (EN)</Label><Input value={catName} onChange={e => setCatName(e.target.value)} /></div>
                <div><Label>Name (AR)</Label><Input value={catNameAr} onChange={e => setCatNameAr(e.target.value)} dir="rtl" /></div>
                <div><Label>Icon (emoji)</Label><Input value={catIcon} onChange={e => setCatIcon(e.target.value)} className="w-20" /></div>
                <Button onClick={handleAddCategory} className="w-full">Add Category</Button>
              </div>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {categories.length === 0 ? (
            <p className="text-muted-foreground text-sm">No categories yet. Add one to organize your menu.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {categories.map(c => (
                <div key={c.id} className="flex items-center gap-2 bg-muted rounded-lg px-3 py-2">
                  <span>{c.icon}</span>
                  <span className="font-medium text-sm">{c.name}</span>
                  <button onClick={() => onDeleteCategory(c.id)} className="text-destructive hover:text-destructive/80 ml-1">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Menu items section */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Products ({menuItems.length})</CardTitle>
          <Dialog open={itemOpen} onOpenChange={(open) => { setItemOpen(open); if (!open) resetItemForm(); }}>
            <DialogTrigger asChild>
              <Button size="sm"><Plus className="w-4 h-4 mr-2" /> Add Product</Button>
            </DialogTrigger>
            <DialogContent className="max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{editingItem ? "Edit Product" : "New Product"}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Name (EN)</Label><Input value={itemName} onChange={e => setItemName(e.target.value)} /></div>
                  <div><Label>Name (AR)</Label><Input value={itemNameAr} onChange={e => setItemNameAr(e.target.value)} dir="rtl" /></div>
                </div>
                <div><Label>Description (EN)</Label><Textarea value={itemDesc} onChange={e => setItemDesc(e.target.value)} /></div>
                <div><Label>Description (AR)</Label><Textarea value={itemDescAr} onChange={e => setItemDescAr(e.target.value)} dir="rtl" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Price</Label><Input type="number" step="0.01" value={itemPrice} onChange={e => setItemPrice(e.target.value)} /></div>
                  <div>
                    <Label>Category</Label>
                    <Select value={itemCategory} onValueChange={setItemCategory}>
                      <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                      <SelectContent>
                        {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.icon} {c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label>Image URL</Label><Input value={itemImage} onChange={e => setItemImage(e.target.value)} placeholder="https://..." /></div>
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2"><Switch checked={itemPopular} onCheckedChange={setItemPopular} /><Label>Best Seller</Label></div>
                  <div className="flex items-center gap-2"><Switch checked={itemAvailable} onCheckedChange={setItemAvailable} /><Label>Active</Label></div>
                </div>
                <Button onClick={handleSaveItem} className="w-full">{editingItem ? "Save Changes" : "Add Product"}</Button>
              </div>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {menuItems.length === 0 ? (
            <p className="text-muted-foreground text-sm">No products yet. Add categories first, then create products.</p>
          ) : (
            <div className="space-y-2">
              {menuItems.map(item => (
                <div key={item.id} className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-muted/30 transition-colors">
                  <div className="flex items-center gap-3">
                    {item.image_url && <img src={item.image_url} alt={item.name} className="w-12 h-12 rounded-lg object-cover" />}
                    <div>
                      <div className="font-medium flex items-center gap-2">
                        {item.name}
                        {item.best_seller && <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">Best Seller</span>}
                        {!item.is_active && <span className="text-xs bg-destructive/10 text-destructive px-2 py-0.5 rounded-full">Inactive</span>}
                      </div>
                      <p className="text-sm text-muted-foreground">{item.description?.slice(0, 60)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold">${Number(item.price).toFixed(2)}</span>
                    <Button variant="ghost" size="icon" onClick={() => openEditItem(item)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => onDeleteItem(item.id)} className="text-destructive">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
